import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useTheme } from '../../context/ThemeContext';
import { useStory } from '../../context/StoryContext';
import { useAuth } from '../../context/AuthContext';

const Navbar = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { state, setNarrativeStyle } = useStory();
  const { user, signOut } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/dashboard', label: 'Storyboard', icon: FiIcons.FiGrid },
    { path: '/story', label: 'Narrative', icon: FiIcons.FiBook },
    { path: '/archive', label: 'Archive', icon: FiIcons.FiArchive },
    { path: '/analytics', label: 'Analytics', icon: FiIcons.FiBarChart2 },
    {
      label: 'Settings',
      icon: FiIcons.FiSettings,
      subLinks: [
        { path: '/profile', label: 'Account', icon: FiIcons.FiUser },
        { path: '/setup/world', label: 'Setup', icon: FiIcons.FiSettings },
      ]
    },
  ];

  return (
    <nav className="border-b border-gray-200 dark:border-purple-800 bg-white/95 dark:bg-purple-950/95 backdrop-blur-md sticky top-0 z-50 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center space-x-8">
            <Link to="/" className="flex items-center space-x-2 group flex-shrink-0">
              <SafeIcon icon={FiIcons.FiFeather} className="text-amber-600 dark:text-amber-500 text-2xl group-hover:rotate-12 transition-transform" />
              <span className="font-serif font-bold text-xl tracking-wide text-gray-900 dark:text-white">Weaver</span>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden md:flex items-center space-x-1">
              {navLinks.map(({ path, label, icon, subLinks }) => {
                if (subLinks) {
                  const isSubActive = subLinks.some(link => isActive(link.path));
                  return (
                    <div key={label} className="relative group">
                      <div
                        className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all cursor-default ${
                          isSubActive
                            ? 'text-amber-600 bg-amber-50 dark:bg-amber-900/20'
                            : 'text-gray-900 dark:text-purple-500 hover:text-purple-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-purple-900/30'
                        }`}
                      >
                        <SafeIcon icon={icon} className="text-sm" />
                        <span>{label}</span>
                        <SafeIcon icon={FiIcons.FiChevronDown} className="text-sm ml-1 opacity-70 group-hover:rotate-180 transition-transform" />
                      </div>
                      <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-purple-900 border border-gray-200 dark:border-purple-800 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50 overflow-hidden">
                        {subLinks.map(sub => (
                          <Link
                            key={sub.path}
                            to={sub.path}
                            className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium transition-colors ${
                              isActive(sub.path)
                                ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-600'
                                : 'text-gray-900 dark:text-purple-300 hover:bg-gray-50 dark:hover:bg-purple-800'
                            }`}
                          >
                            <SafeIcon icon={sub.icon} />
                            <span>{sub.label}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={path}
                    to={path}
                    className={`flex items-center space-x-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all ${
                      isActive(path)
                        ? 'text-amber-600 bg-amber-50 dark:bg-amber-900/20'
                        : 'text-gray-900 dark:text-purple-500 hover:text-purple-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-purple-900/30'
                    }`}
                  >
                    <SafeIcon icon={icon} className="text-sm" />
                    <span>{label}</span>
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Right Side Controls */}
          <div className="flex items-center space-x-3">
            {/* Adventurer / Writer Toggle — only on /story */}
            {location.pathname === '/story' && (
              <div className="hidden sm:flex bg-gray-50 dark:bg-purple-900/50 p-1 rounded-xl border border-gray-200 dark:border-purple-800 shadow-inner">
                <button
                  onClick={() => setNarrativeStyle('adventurer')}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                    state.narrativeStyle === 'adventurer'
                      ? 'bg-white dark:bg-purple-800 text-amber-600 shadow-sm'
                      : 'text-gray-900 dark:text-purple-400 hover:text-gray-900 dark:hover:text-purple-200'
                  }`}
                >
                  <SafeIcon icon={FiIcons.FiTarget} />
                  <span>Adventurer</span>
                </button>
                <button
                  onClick={() => setNarrativeStyle('writer')}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                    state.narrativeStyle === 'writer'
                      ? 'bg-white dark:bg-purple-800 text-amber-600 shadow-sm'
                      : 'text-gray-900 dark:text-purple-400 hover:text-gray-900 dark:hover:text-purple-200'
                  }`}
                >
                  <SafeIcon icon={FiIcons.FiEdit2} />
                  <span>Writer</span>
                </button>
              </div>
            )}

            {/* Auth Link */}
            {user ? (
              <button
                onClick={() => signOut()}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-purple-800 text-gray-900 dark:text-purple-400 transition-colors text-sm font-semibold"
                title="Sign Out"
              >
                <SafeIcon icon={FiIcons.FiLogOut} />
              </button>
            ) : (
              <Link
                to="/auth"
                className="px-3 py-1.5 rounded-lg text-sm font-semibold bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-400 transition-colors"
              >
                Sign In
              </Link>
            )}

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-purple-800 text-gray-900 dark:text-purple-400 transition-colors"
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              <SafeIcon icon={isDarkMode ? FiIcons.FiSun : FiIcons.FiMoon} />
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-purple-800 text-gray-900 dark:text-purple-400 transition-colors"
            >
              <SafeIcon icon={mobileOpen ? FiIcons.FiX : FiIcons.FiMenu} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Dropdown */}
      {mobileOpen && (
        <div className="md:hidden border-t border-gray-100 dark:border-purple-800 bg-white dark:bg-purple-950 px-4 py-3 space-y-1">
          {navLinks.map(({ path, label, icon, subLinks }) => {
            let currentSubLinks = subLinks;
            if (label === 'Narrative') {
              currentSubLinks = [
                { path: '/story?tab=quests', label: 'Quests', icon: FiIcons.FiTarget },
                { path: '/story?tab=chapters', label: 'Chapters', icon: FiIcons.FiBookOpen },
                ...(state.ownerId === user?.id ? [{ path: '/story?tab=ideas', label: 'Ideas', icon: FiIcons.FiZap }] : []),
                { path: '/story?tab=gallery', label: 'Gallery', icon: FiIcons.FiImage },
                { path: '/story?tab=recovery', label: 'Recovery', icon: FiIcons.FiTrash2 },
              ];
            }

            if (currentSubLinks) {
              return (
                <div key={label} className="space-y-1">
                  {label === 'Narrative' ? (
                    <Link
                      to={path}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center space-x-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                        isActive(path) && !location.search
                          ? 'text-amber-600 bg-amber-50 dark:bg-amber-900/20'
                          : 'text-gray-900 dark:text-purple-500 hover:text-purple-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-purple-900/30'
                      }`}
                    >
                      <SafeIcon icon={icon} />
                      <span>{label}</span>
                    </Link>
                  ) : (
                    <div className="flex items-center space-x-2 px-3 py-2.5 text-sm font-semibold text-gray-900 dark:text-purple-400 uppercase tracking-wider">
                      <SafeIcon icon={icon} />
                      <span>{label}</span>
                    </div>
                  )}
                  <div className="pl-6 space-y-1">
                    {currentSubLinks.map(sub => {
                      const isSubActive = sub.path.includes('?')
                        ? location.pathname + location.search === sub.path
                        : isActive(sub.path);

                      return (
                        <Link
                          key={sub.path}
                          to={sub.path}
                          onClick={() => setMobileOpen(false)}
                          className={`flex items-center space-x-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                            isSubActive
                              ? 'text-amber-600 bg-amber-50 dark:bg-amber-900/20'
                              : 'text-gray-900 dark:text-purple-500 hover:text-purple-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-purple-900/30'
                          }`}
                        >
                          <SafeIcon icon={sub.icon} />
                          <span>{sub.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            }

            return (
              <Link
                key={path}
                to={path}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center space-x-2 px-3 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                  isActive(path)
                    ? 'text-amber-600 bg-amber-50 dark:bg-amber-900/20'
                    : 'text-gray-900 dark:text-purple-500 hover:text-purple-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-purple-900/30'
                }`}
              >
                <SafeIcon icon={icon} />
                <span>{label}</span>
              </Link>
            );
          })}
          {location.pathname === '/story' && (
            <div className="flex bg-gray-50 dark:bg-purple-900/50 p-1 rounded-xl border border-gray-200 dark:border-purple-800 mt-2">
              <button
                onClick={() => { setNarrativeStyle('adventurer'); setMobileOpen(false); }}
                className={`flex-1 flex items-center justify-center space-x-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                  state.narrativeStyle === 'adventurer'
                    ? 'bg-white dark:bg-purple-800 text-amber-600 shadow-sm'
                    : 'text-gray-600'
                }`}
              >
                <SafeIcon icon={FiIcons.FiTarget} /><span>Adventurer</span>
              </button>
              <button
                onClick={() => { setNarrativeStyle('writer'); setMobileOpen(false); }}
                className={`flex-1 flex items-center justify-center space-x-1.5 px-3 py-1.5 text-xs font-bold rounded-lg transition-all ${
                  state.narrativeStyle === 'writer'
                    ? 'bg-white dark:bg-purple-800 text-amber-600 shadow-sm'
                    : 'text-gray-600'
                }`}
              >
                <SafeIcon icon={FiIcons.FiEdit2} /><span>Writer</span>
              </button>
            </div>
          )}
          {/* Legal links in mobile menu */}
          <div className="flex space-x-2 pt-2 border-t border-gray-100 dark:border-purple-800 mt-2">
            {[
              { path: '/privacy', label: 'Privacy' },
              { path: '/terms', label: 'Terms' },
              { path: '/support', label: 'Support' },
            ].map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                onClick={() => setMobileOpen(false)}
                className="text-xs text-gray-900 dark:text-purple-400 hover:text-amber-600 font-medium px-2 py-1"
              >
                {label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;